import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  hasSupabaseConfig,
  initialPasswordRecoveryIntent,
  supabase,
} from '../../services/supabase/client.js';

function recoveryMarkerPresent() {
  if (typeof window === 'undefined') return false;
  const marker = `${window.location.search || ''}&${window.location.hash || ''}`;
  return /(?:[?#&]|^)type=recovery(?:&|$)/i.test(marker);
}

const SessionContext = createContext({
  ready: false,
  configured: false,
  session: null,
  user: null,
  recoveryMode: false,
});

export function SessionProvider({ children }) {
  const [ready, setReady] = useState(!hasSupabaseConfig);
  const [session, setSession] = useState(null);
  const [recoveryMode, setRecoveryMode] = useState(
    initialPasswordRecoveryIntent || recoveryMarkerPresent(),
  );

  useEffect(() => {
    if (!supabase) return undefined;

    let active = true;
    supabase.auth.getSession()
      .then(({ data, error }) => {
        if (error) console.warn('[WatchVIM V3] Session bootstrap failed', error);
        if (active) {
          setSession(data?.session ?? null);
          setReady(true);
        }
      })
      .catch((error) => {
        console.warn('[WatchVIM V3] Session bootstrap failed', error);
        if (active) setReady(true);
      });

    const { data: listener } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!active) return;
      if (event === 'PASSWORD_RECOVERY') setRecoveryMode(true);
      if (event === 'SIGNED_OUT') setRecoveryMode(false);
      setSession(nextSession ?? null);
      setReady(true);
    });

    return () => {
      active = false;
      listener?.subscription?.unsubscribe();
    };
  }, []);

  const value = useMemo(() => ({
    ready,
    configured: hasSupabaseConfig,
    session,
    user: session?.user ?? null,
    recoveryMode,
  }), [ready, session, recoveryMode]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  return useContext(SessionContext);
}
